import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils.ts";

export const MealsSection: React.FC = () => {
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);

  useEffect(() => {
    function handleVideoExpansion() {
      if (gridRef.current) {
        const container = gridRef.current;
        const containerRect = container.getBoundingClientRect();
        // Check if grid is in the viewport
        if (containerRect.top <= 0 && Math.abs(containerRect.top) < container.clientHeight) {
          // Calculate the scroll percentage inside the container (how much of the grid container is scrolled)
          const scrollPercentage = Math.abs(containerRect.top) / container.clientHeight;
          if (scrollPercentage >= 0.5) {
            setIsVideoExpanded(true);
          } else {
            setIsVideoExpanded(false);
          }
        }
      }
    }

    // throttle the scroll event to improve performance
    let ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleVideoExpansion();
          ticking = false; // reset ticking after the animation frame is executed
        });
      }
      ticking = true; // set ticking to true when scroll event is fired
    }

    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Play and pause video based on visibility in the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          videoRef.current?.play();
        } else {
          videoRef.current?.pause();
        }
      },
      { threshold: 0.5 },
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section className={`mt-16 py-6 md:min-h-[300vh]`}>
      <h1 className="px-6 text-2xl font-semibold md:text-5xl">
        {t("aboutPage.mealsSection.title")}
      </h1>
      <div
        className={`relative order-1 mt-8 grid h-[2470px] grid-cols-2 flex-col px-4 md:order-none md:grid md:h-[300vh] md:grid-cols-5 md:flex-row md:px-6`}
        ref={gridRef}
      >
        {/* First Column */}
        <div>
          <div className={`px-2 md:w-auto md:px-0`}>
            <TextCard cardKey="card1" />
            <ImageCard cardKey="card2" image="/meals-1.jpeg" className="mt-4" />
          </div>
          <div className={`mt-8 px-2 md:w-auto md:px-0`}>
            <TextCard cardKey="card9" />
            <ImageCard cardKey="card10" image="/meals-1.jpeg" className="mt-4" />
          </div>
        </div>

        {/* Second Column */}
        <div>
          <div className={`order-3 mt-12 px-2 md:order-none md:w-auto`}>
            <ImageCard cardKey="card3" image="/meals-2.png" />
            <TextCard cardKey="card4" className="mt-4" />
          </div>
          <div className={`order-3 mt-12 px-2 md:order-none md:w-auto`}>
            <ImageCard cardKey="card11" image="/meals-2.png" />
            <TextCard cardKey="card12" className="mt-4" />
          </div>
        </div>
        {/* Video Column (Desktop) */}
        <div className="sticky top-0 z-20 hidden h-[calc(80vh+8rem)] self-start md:order-none md:block">
          <video
            ref={videoRef}
            src="/meals.mp4"
            preload="metadata"
            controls
            loop
            muted
            autoPlay
            className={cn(
              "object-cover z-20 max-w-none w-full absolute top-32 lef-0 flex min-h-80 h-[320px] duration-1000 transition-[width,height,margin] ease-in-out flex-col justify-end rounded-lg bg-cover bg-center",
              isVideoExpanded && "w-[calc(100vw-48px)] h-[80svh] ms-[calc(-50vw+50%+24px)]",
            )}
          />
        </div>
        {/* Third Column */}
        <div>
          <div className={`order-2 mt-4 px-2 md:order-none md:mt-12 md:w-auto`}>
            <TextCard cardKey="card5" />
            <ImageCard cardKey="card6" image="/meals-3.jpeg" className="mt-4" />
          </div>
          <div className={`order-2 mt-4 px-2 md:order-none md:mt-12 md:w-auto`}>
            <TextCard cardKey="card13" />
            <ImageCard cardKey="card14" image="/meals-3.jpeg" className="mt-4" />
          </div>
        </div>

        {/* Fourth Column */}
        <div>
          <div className={`order-4 mt-4 px-2 md:order-none md:mt-0 md:w-auto md:px-0`}>
            <ImageCard cardKey="card7" image="/meals-4.jpeg" />
            <TextCard cardKey="card8" className="mt-4" />
          </div>
          <div className={`order-4 mt-4 px-2 md:order-none md:mt-12 md:w-auto md:px-0`}>
            <ImageCard cardKey="card15" image="/meals-4.jpeg" />
            <TextCard cardKey="card16" className="mt-4" />
          </div>
        </div>
      </div>
      {/* Mobile Video */}
      <div className="md:hidden">
        <div
          className={`mx-auto flex h-[80vh] w-[calc(100vw-48px)] flex-col justify-end rounded-lg bg-cover bg-center transition-all duration-500 ease-in-out`}
        >
          <video
            preload="metadata"
            playsInline
            src="/meals.mp4"
            controls
            loop
            muted
            autoPlay
            className="z-40 h-full w-full rounded-lg object-cover"
          />
        </div>
      </div>
    </section>
  );
};

interface TextCardProps {
  cardKey: string;
  className?: string;
}

export function TextCard({ cardKey, className }: Readonly<TextCardProps>) {
  const { t } = useTranslation();

  // Check if the card should be visible
  if (t(`aboutPage.mealsSection.cards.${cardKey}.visible`) !== "true") {
    return null;
  }

  return (
    <dl
      className={cn("flex h-60 w-full flex-col justify-end rounded-lg bg-[#E2DDD7] p-4", className)}
    >
      <dd className="text-3xl md:text-4xl lg:text-5xl">
        {t(`aboutPage.mealsSection.cards.${cardKey}.amount`)}
      </dd>
      <dt>{t(`aboutPage.mealsSection.cards.${cardKey}.label`)}</dt>
    </dl>
  );
}

interface ImageCardProps {
  cardKey: string;
  image: string;
  className?: string;
}

export function ImageCard({ cardKey, image, className }: Readonly<ImageCardProps>) {
  const { t } = useTranslation();

  // Check if the card should be visible
  if (t(`aboutPage.mealsSection.cards.${cardKey}.visible`) !== "true") {
    return null;
  }

  return (
    <div
      className={cn(
        `relative flex h-80 w-full flex-col justify-end rounded-lg overflow-hidden p-4`,
        className,
      )}
    >
      <img
        src={image}
        alt={t(`aboutPage.mealsSection.cards.${cardKey}.label`)}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/50"></div>
      <dl className="relative z-10">
        <dd className="text-3xl text-[#E2DDD7] md:text-4xl lg:text-5xl ">
          {t(`aboutPage.mealsSection.cards.${cardKey}.amount`)}
        </dd>
        <dt className="text-[#E2DDD7]">{t(`aboutPage.mealsSection.cards.${cardKey}.label`)}</dt>
      </dl>
    </div>
  );
}
